<template>
    <div
        ref="item"
        :class="getItemClass"
        @click="openModel"
    >
        <div class="catalog-item__content">
            <div
                v-if="currentViewMode !== 'mode-3' && !isCustom"
                class="catalog-item__content__header"
            >
                <ModelStatusLabel
                    :model="item"
                />
                <div class="header-block">
                  <div
                      v-if="showCulture"
                      class="flag"
                      :src="flag"
                      :style="getFlagImage"
                  />
                  <CatalogActions :knowledge-model="$props.item" />
                </div>
            </div>
            <div
                v-if="currentViewMode !== 'mode-3' && isCustom"
                class="catalog-item__content__header"
            >
                <div class="engine-label">
                    {{engineLabel}}
                </div>
              <div class="header-block">
                <CatalogActions :knowledge-model="$props.item" is-custom />
              </div>
            </div>
            <div class="catalog-item__content__icon">
                <div
                    class="img-wrap"
                    :style="getBgImage"
                />
            </div>
            <div
                v-show="currentViewMode !== 'mode-3'"
                class="catalog-item__content__info"
            >
                <p class="label">{{item.name}}</p>
                <p class="description">{{isCustom ? item.description : item.desc}}</p>
            </div>
            <div
                v-show="currentViewMode === 'mode-1'"
                class="catalog-item__content__modified"
            >
                <p class="text" v-if="alreadyCreated">
                  {{ $t('home.catalog_item.createdBy', { 'date': modifiedByLabel }) }}
                  <br>
                  {{createdDate}}
                </p>
                <p class="text" v-else>{{ $t('home.catalog_item.modifiedBy', { 'date': modifiedByLabel }) }}<br>
                    {{modifiedTime}}
                </p>
            </div>
            <div v-if="currentViewMode === 'mode-3'"
                 class="catalog-item__content__row-template"
            >
                <div class="catalog-item__content__row-template__left-col">
                    <p class="label">{{item.name}}</p>
                    <model-status :id="item.id" />
                </div>
                <div class="catalog-item__content__row-template__center-col">
                    <p class="description">{{item.desc}}</p>
                </div>
                <div class="catalog-item__content__row-template__right-col">
                    <p class="text" v-if="alreadyCreated">
                      {{ $t('home.catalog_item.createdBy', { 'date': modifiedByLabel }) }}
                      <br>
                      {{createdDate}}
                    </p>
                    <p class="text" v-if="!alreadyCreated">
                      {{ $t('home.catalog_item.modifiedBy', { 'date': modifiedByLabel }) }}
                      <br>
                      {{modifiedTime}}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { flags } from '@/modules/language-flags-mapping'
import ModelStatusLabel from '@/components/common/statuses/model_status_label'
import CatalogActions from './catalog_actions'
import get from 'lodash/get'

export default {
  components: {
    ModelStatusLabel,
    CatalogActions
  },

  props: {
    item: Object,
    index: Number,
    currentViewMode: {
      type: String,
      default: 'mode-1'
    }
  },

  computed: {
    ...mapState('knowledgebase/users', ['users']),
    ...mapState('knowledgebase/flows', ['flows']),
    ...mapState('knowledgebase/customModels', ['customEngines']),

    modifiedByLabel () {
      let user, flow
      user = flow = undefined

      const label = this.$t('home.catalog_item.unknownFlow')

      if (!this.users.loading && !this.flows.loading && this.$props.item.modifiedBy) {
        user = this.users.value.find(item => item.id === this.$props.item.modifiedBy)
        flow = this.flows.value.find(item => item.id === this.$props.item.modifiedBy)
      }

      if (user) {
        return get(user, 'username', label)
      }

      if (flow) {
        const flowLabel = get(flow, 'data.label')
        return flowLabel ? this.$t('home.catalog_item.flowType', { flowType: flowLabel }) : label
      }

      return label
    },

    selectedEngine () {
      return this.customEngines.find(engine => engine.id === this.item.engine)
    },

    engineLabel () {
      return this.selectedEngine ? this.selectedEngine.label : ''
    },

    modelExtensions () {
      return this.item.extensions
    },

    showCulture () {
      return !_.isEmpty(this.modelExtensions)
    },

    flag () {
      const flagObj = flags.find(f => f.value === this.item.culture.toLowerCase())
      return flagObj ? flagObj.flag : ''
    },

    getFlagImage () {
      const styleObjectOne = {
        width: '25px',
        height: '15px',
        'background-image': `url(${this.flag})`
      }
      const styleObjectTwo = {
        right: '0',
        width: '18px',
        height: '18px',
        'background-image': `url(${this.flag})`
      }
      let style = {}

      if ((this.currentViewMode === 'mode-1') || (this.currentViewMode === 'mode-2')) {
        style = styleObjectOne
      }
      if (this.currentViewMode === 'mode-3') {
        style = styleObjectTwo
      }

      return style
    },

    modifiedTime () {
      return moment(this.item.modifiedDate * 1000).startOf('minutes').fromNow()
    },

    createdDate () {
      return moment(this.item.createdDate * 1000).startOf('minutes').fromNow()
    },

    getItemClass () {
      return `catalog-item catalog-item--${this.currentViewMode} ${this.item.isOld ? 'is-old' : ''}`
    },

    alreadyCreated () {
      return this.item.modifiedDate === this.item.createdDate
    },

    engineImage () {
      return get(this.selectedEngine, 'image', '')
    },

    getBgImage () {
      let backgroundImage

      if (this.isCustom) {
        const defaultEngineImage = this.item.engine ? this.engineImage : ''
        backgroundImage = `url(${this.item.img}), url(${defaultEngineImage})`
      } else {
        const url = this.item.imageSrc
          ? this.item.imageSrc
          : 'https://sdkapi-qa.onereach.ai/bot-face/Agent%20Assistant?color=%23FFFFFF'

        backgroundImage = `url(${url})`
      }

      return {
        backgroundImage
      }
    },

    isCustom () {
      return _.has(this.item, 'engine')
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['initNluModel']),

    openModel () {
      this.initNluModel(this.item.id)

      this.$router.push({
        name: 'model',
        params: {
          id: this.item.id
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
    .catalog-item {
        position: relative;
        flex-shrink: 0;
        padding: 16px;
        background-color: $or-white;
        border: 1px solid transparent;
        border-radius: 3px;
        transition: all .3s ease;
        box-shadow: 0 1px 2px 0 rgba(0,0,0,0.2);

        &.is-old {
            border-top: solid $or-yellow 5px;
        }

        &:hover {
            cursor: pointer;
            box-shadow: 0 1px 4px 1px rgba(0,0,0,0.2);
        }

        &__stat{
            position: absolute;
            background: darkkhaki;
        }

        &__content {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;

            .header {
                display: flex;
                justify-content: flex-end;
            }

            &__header {
                display: flex;
                justify-content: space-between;
                width: 100%;
                margin-bottom: 14px;
                .header-block {
                  display: flex;
                  margin-right: -14px;
                }

                .flag {
                    background-size: cover;
                    background-repeat: no-repeat;
                    background-position: center;
                }

                .engine-label {
                    font-size: 12px;
                    color: #91969D;
                }

                .brain-icon > path {
                    transition: all 0.1s ease;
                    fill: $or-grey;
                }

                .brain-icon:hover {
                    path {
                        transition: all 0.1s ease;
                        fill: $or-black-blue;
                    }
                }
            }

            &__icon {
                display: flex;
                width: 100%;
                height: 100%;
                justify-content: center;
                align-items: center;

                .img-wrap {
                    position: relative;
                    width: 100px;
                    height: 100px;
                    border-radius: 100px;
                    background-repeat: no-repeat;
                    background-position: center;
                    background-size: cover;
                }
            }

            &__info {
                width: 100%;
                height: 100%;
                margin-top: 16px;
                overflow: hidden;
                color: $or-black-blue;

                .label, .description {
                    font-size: 14px;
                    line-height: 16px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-align: center;
                }

                .label {
                    margin: 0;
                    font-weight: bold;
                    white-space: nowrap;
                }

                .description {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    margin: 8px 0 0;
                    font-weight: normal;
                    word-break: break-all;
                }
            }

            &__modified {
                width: 100%;

                .text {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    margin: 0;
                    text-align: left;
                    font-size: 12px;
                    line-height: 14px;
                    color: $or-grey;
                }
            }

            &__row-template {
                display: flex;
                justify-content: space-between;
                width: 100%;
                height: 100%;
                padding: 16px 0;

                &__left-col, &__center-col, &__right-col {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                &__left-col, &__center-col {
                    width: 25%;
                    margin-right: 16px;
                }

                &__left-col {
                    min-width: 100px;

                    .label {
                        margin: 0;
                        font-size: 14px;
                        line-height: 16px;
                        font-weight: bold;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                &__center-col {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex-wrap: nowrap;

                    .description {
                        display: -webkit-box;
                        margin: 0;
                        font-size: 14px;
                        line-height: 16px;
                        font-weight: normal;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                &__right-col {
                    margin: -16px 0 0;
                    overflow: hidden;

                    .brain-icon {
                        margin-left: calc(100% - 20px);

                        path {
                            fill: $or-grey;
                            transition: all 0.1s ease;
                        }
                    }

                    .brain-icon:hover{
                        path {
                            fill: $or-black-blue;
                        }
                    }

                    .text {
                        text-align: right;
                        margin: 0;
                        font-size: 12px;
                        line-height: 14px;
                        color: $or-grey;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }
        }

        &--mode-1 {
            height: 300px;
            margin-right: 20px;
            margin-bottom: 20px;

            &:last-child {
                margin-right: auto;
            }

            @media (max-width: 576px) {
                width: 100%;
                margin-right: 0;
            }

            @media (min-width: 576px) and (max-width: 991px) {
                width: calc(100% / 3 - 14px);

                &:nth-child(3n) {
                    margin-right: 0;
                }
            }

            @media (min-width: 992px) and (max-width: 1200px) {
                width: calc(100% / 4 - 15px);

                &:nth-child(4n) {
                    margin-right: 0;
                }
            }

            @media (min-width: 1201px)  {
                width: calc(100% / 5 - 16px);

                &:nth-child(5n) {
                    margin-right: 0;
                }
            }

            .catalog-item__content {
                @keyframes fadein {
                    from {
                        opacity: 0;
                    }

                    to {
                        opacity: 100%;
                    }
                }

                animation-name: fadein;
                animation-duration: .8s;
                animation-timing-function: ease-in;
            }
        }

        &--mode-2 {
            height: 150px;
            margin-right: 20px;
            margin-bottom: 20px;

            &:last-child {
                margin-right: auto;
            }

            @media (max-width: 575px) {
                width: 100%;
                margin-right: 0;
            }

            @media (min-width: 576px) and (max-width: 991px) {
                width: calc(100% / 2 - 10px);

                &:nth-child(2n) {
                    margin-right: 0;
                }
            }

            @media (min-width: 992px) and (max-width: 1200px) {
                width: calc(100% / 3 - 14px);

                &:nth-child(3n) {
                    margin-right: 0;
                }
            }

            @media (min-width: 1201px)  {
                width: calc(100% / 4 - 15px);

                &:nth-child(4n) {
                    margin-right: 0;
                }
            }

            .catalog-item__content {
                animation-name: fadein-2;
                animation-duration: .8s;
                animation-timing-function: ease-in;

                @keyframes fadein-2 {
                    from {
                        opacity: 0;
                    }

                    to {
                        opacity: 100%;
                    }
                }

                &__header {
                    margin: 0;
                }

                &__icon > .img-wrap {
                    width: 32px;
                    height: 32px;
                }

                &__info {
                    margin-top: 0;

                    .description {
                        margin: 8px 0 0;
                        -webkit-line-clamp: 1;
                        font-size: 12px;
                        color: $or-grey;
                    }
                }
            }
        }

        &--mode-3 {
            width: 100%;
            height: 112px;
            margin-bottom: 20px;

            .catalog-item__content {
                flex-direction: row;
                animation-name: fadein-3;
                animation-duration: .8s;
                animation-timing-function: ease-in;

                @keyframes fadein-3 {
                    from {
                        opacity: 0;
                    }

                    to {
                        opacity: 100%;
                    }
                }

                &__icon {
                    width: 80px;
                    margin-right: 32px;

                    .img-wrap {
                        width: 80px;
                        height: 80px;
                    }
                }
            }
        }
    }
</style>
